// Who I am here (artin 11-02): the header of 「设置」 (public/settings.js) and the server's name in the top bar.
// The header: my otter (or my Raft picture), my name, my status, the Raft server this Deck is (name + slug), the other
// servers this browser is signed in to (one tap switches: POST /auth/switch, no new sign-in), 切换 Server / 添加 Server
// (a Raft sign-in: Raft asks which server; the session I had stays in the list) and 退出登录. A guest (访客) has no
// Raft server of their own here: 离开 Deck, and no switching. How the sessions are kept: src/auth.ts (top).
// The top bar's tag: the server's name, quiet, truncated on a phone; a click opens 设置.
import { icon, label, put } from '/icons.js';
import { t, onLang } from '/i18n.js';

const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
/** What a server is called: its name, else its slug. */
export const serverLabel = (s) => (s?.name || s?.slug || '').trim();

/**
 * @param o {name, picture (a Raft picture URL or null), server ({id, slug, name}, null for a guest), guest ({by, exp} or null),
 *   others() ([{key, id, slug, name, who}]: my other servers in this browser, GET /me), switchTo(key), addServer(hint?) (or null:
 *   a guest), signOut ({label, run}: every server in this browser), signOutThis() (this server only, when there are others), signOutAll() (every session of mine on this server, on every device; null for a guest), avatar() (a canvas of my otter, or null
 *   before I'm aboard), status() ({icon, text}: my status now), settings ({setHeader, refreshHeader, open}:
 *   public/settings.js, passed in by app.js: one settings module for the page),
 *   visit ({server_name, from}: this page is visiting another server's office, 串门, public/visits.js; server is then my own) and goHome()}
 * @returns {{refresh()}} refresh: my status or look changed (redraws the header if 设置 is open)
 */
export function initProfile(o) {
  const srv = o.server ? serverLabel(o.server) || 'Raft server' : '';
  const slugOf = (s) => (s.slug && s.slug !== serverLabel(s) ? s.slug : '');
  let adding = false, addHint = '';

  const { setHeader, refreshHeader, open: openSettings } = o.settings;
  setHeader((box) => {
    const row = el('div', 'pf');
    const av = el('div', 'pf-av');
    const c = (() => { try { return o.avatar?.(); } catch { return null; } })();
    if (c) av.append(c);
    else if (o.picture) { const img = el('img'); img.alt = ''; img.referrerPolicy = 'no-referrer'; img.src = o.picture; img.onerror = () => img.replaceWith(icon('otter', { size: 32 })); av.append(img); }
    else av.append(icon('otter', { size: 32 }));
    const whoBox = el('div', 'pf-who');
    const nm = el('div', 'pf-name', o.name || t('profile.me')); nm.title = o.name || '';
    const st = (() => { try { return o.status?.(); } catch { return null; } })(), stl = el('div', 'pf-st');
    put(stl, st?.icon, st?.text ?? ''); stl.title = st?.text ?? '';
    whoBox.append(nm, stl);
    row.append(av, whoBox);
    box.append(row);

    const line = el('div', 'pf-srv');
    if (o.server) {
      line.append(icon('people'), el('span', '', t('profile.server')));
      const b = el('b', '', srv); b.title = srv; line.append(b);
      const sl = slugOf(o.server); if (sl) { const s = el('span', 'slug', sl); s.title = sl; line.append(s); }
    } else {
      line.append(icon('people'), el('span', 'g', t('profile.guest', { by: o.guest?.by || t('profile.guestBySomeone') })));
    }
    box.append(line);
    if (o.visit) { // 串门: where I am now, and the way back
      const v = el('div', 'pf-srv');
      v.append(icon('pin'), el('span', '', t('profile.visit.title')));
      const b = el('b', '', o.visit.server_name || t('profile.visit.otherServer')); b.title = o.visit.server_name || ''; v.append(b);
      const h = el('button', '', t('profile.visit.home')); h.type = 'button'; h.dataset.act = 'go-home'; h.title = t('profile.visit.homeTip');
      h.style.cssText = 'margin-left:auto;flex:none;font:inherit;font-size:12px;border:2px solid #1b1410;border-radius:999px;padding:1px 10px;background:#FDD03B;cursor:pointer';
      h.onclick = () => { h.disabled = true; o.goHome?.(); };
      v.append(h);
      box.append(v);
    }

    // my other servers in this browser: one tap switches
    const others = o.server ? o.others?.() ?? [] : [];
    if (others.length) {
      const list = el('div', 'pf-list');
      list.append(el('div', 'pf-lab', t('profile.others')));
      for (const s of others) {
        const r = el('div', 'pf-row');
        const n = el('b', '', serverLabel(s) || 'Raft server'); n.title = serverLabel(s);
        r.append(icon('people'), n);
        const sl = slugOf(s); if (sl) r.append(el('span', 'slug', sl));
        if (s.who && s.who !== o.name) r.append(el('span', 'slug', s.who));
        const go = el('button', '', t('profile.switch')); go.type = 'button'; go.dataset.act = 'switch-to'; go.dataset.key = s.key;
        go.title = t('profile.switchTip', { server: serverLabel(s) });
        go.onclick = () => { go.disabled = true; Promise.resolve(o.switchTo(s.key)).finally(() => { go.disabled = false; }); };
        r.append(go);
        list.append(r);
      }
      box.append(list);
    }

    const acts = el('div', 'pf-acts');
    if (o.addServer) {
      const sw = label(el('button', 'main'), 'people', t('profile.add')); sw.type = 'button'; sw.dataset.act = 'add';
      sw.title = t('profile.addTip');
      sw.setAttribute('aria-expanded', String(adding));
      if (adding) sw.setAttribute('aria-controls', 'pfAddServerForm');
      sw.onclick = () => { adding = !adding; refreshHeader(); if (adding) box.querySelector('#pfAddServer')?.focus(); };
      acts.append(sw);
    }
    const out = label(el('button'), 'door', o.signOut.label); out.type = 'button'; out.dataset.act = 'signout';
    if (others.length) out.title = t('profile.signOutTip');
    out.onclick = () => o.signOut.run();
    acts.append(out);
    if (o.signOutAll) {
      const all = el('button', '', t('profile.signOutAll')); all.type = 'button'; all.dataset.act = 'signout-all';
      all.title = t('profile.signOutAllTip');
      all.onclick = () => o.signOutAll();
      acts.append(all);
    }
    if (others.length && o.signOutThis) {
      const one = el('button', '', t('profile.signOutThis')); one.type = 'button'; one.dataset.act = 'signout-this';
      one.title = t('profile.signOutThisTip');
      one.onclick = () => o.signOutThis();
      acts.append(one);
    }
    box.append(acts);
    if (adding && o.addServer) {
      const form = el('form', 'pf-add'); form.id = 'pfAddServerForm';
      form.append(el('p', '', t('app.addServer.ask')));
      const name = el('label', '', t('profile.addLabel')); name.htmlFor = 'pfAddServer';
      const input = el('input'); input.id = 'pfAddServer'; input.type = 'text'; input.maxLength = 128;
      input.autocomplete = 'off'; input.placeholder = t('profile.addPlaceholder'); input.value = addHint;
      input.setAttribute('aria-describedby', 'pfAddServerError');
      const err = el('p', 'pf-add-error'); err.id = 'pfAddServerError'; err.setAttribute('role', 'alert');
      input.oninput = () => { addHint = input.value; err.textContent = ''; input.removeAttribute('aria-invalid'); };
      const row = el('div', 'pf-add-actions');
      const go = el('button', 'main', t('profile.addContinue')); go.type = 'submit';
      const cancel = el('button', '', t('profile.addCancel')); cancel.type = 'button';
      cancel.onclick = () => { adding = false; addHint = ''; refreshHeader(); box.querySelector('[data-act="add"]')?.focus(); };
      row.append(go, cancel); form.append(name, input, err, row);
      form.onsubmit = (event) => {
        event.preventDefault();
        const hint = input.value.trim().toLowerCase();
        if (hint && !/^[a-z0-9][a-z0-9-]{0,62}$/.test(hint)) {
          err.textContent = t('profile.addInvalid'); input.setAttribute('aria-invalid', 'true'); input.focus(); return;
        }
        try { o.addServer(hint || undefined); }
        catch { err.textContent = t('profile.addFailed'); }
      };
      box.append(form);
    }
  });

  // the top bar: first (the connection readout is no longer a pill there: app.js #net), before the floor picker and 设置
  if (srv && !o.visit) { // (visiting: public/visits.js puts 「在 X 串门 · 回家」 there instead)
    const tag = el('button'); tag.id = 'serverTag'; tag.type = 'button';
    tag.append(icon('people'), el('span', 't', srv));
    const sl = slugOf(o.server);
    const words = () => {
      tag.title = sl ? t('profile.tag.tipSlug', { server: srv, slug: sl }) : t('profile.tag.tip', { server: srv });
      tag.setAttribute('aria-label', t('profile.tag.label', { server: srv }));
    };
    words(); onLang(words);
    tag.onclick = (e) => { e.stopPropagation(); openSettings(e.detail === 0, 'account'); };
    const put1 = () => document.getElementById('topleft')?.prepend(tag); // first in the top bar (the connection readout is in the corner: app.js #net)
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', put1); else put1();
  }
  // Status updates also refresh the header; don't replace the text field while someone is typing a server hint.
  return { refresh: () => { if (!(adding && document.activeElement?.id === 'pfAddServer')) refreshHeader(); } };
}
